import { IconCheck } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { FileItem } from "@/components/elements/FileItem";
import { useFormField } from "@/components/elements/FormField";
import { MIME_TYPES } from "@/constants/file";
import { INVALID_PASSWORD_PROTECTED_PDF, INVALID_PDF_FORMAT } from "@/constants/invalids";
import { background, divider, state } from "@/theme/colors";
import { FCC } from "@/types/common";
import { CHECK_PDF_RESULT } from "@/types/enum";
import { TExtendFile, TExtendFileOption } from "@/types/file";
import {
  checkFileListWhetherIsActualPdf,
  convertByteToMb,
  getExtensionsOfMimeTypes,
  getFileErrorMessage,
  getFileStatusWithRules,
} from "@/utils/file";
import { makeUuid } from "@/utils/pieces";
import { toast } from "@/utils/toast";
import { Stack, styled } from "@mui/material";
import { Key, useCallback, useEffect, useMemo, useState } from "react";

export type TUploadFileBtnProps = {
  nonControl?: boolean;
  readonly?: boolean;
  employeeFirstName: string | null;
  residenceExpiryDate: string | null;
  handleCheckExistFileName: (e: React.MouseEvent<HTMLInputElement, MouseEvent>) => void;
  existingFiles: string[];
  fieldLabel: string;
};

export const UploadFileBtn: FCC<TUploadFileBtnProps> = ({
  nonControl = false,
  handleCheckExistFileName,
  readonly,
  employeeFirstName,
  residenceExpiryDate,
  existingFiles,
  fieldLabel,
}) => {
  const rules = { acceptMimeTypes: [MIME_TYPES.IMAGE, MIME_TYPES.PDF].flat() };

  const { field } = useFormField(nonControl);

  const [files, setFiles] = useState<TExtendFile[]>([field?.value ?? []].flat());

  useEffect(() => {
    if (field?.value) {
      const newValue: TExtendFile[] = [field?.value].flat();
      if (files.some((file) => !newValue.find(({ id }) => file.id === id))) {
        setFiles([field?.value].flat());
      }
    } else {
      setFiles([]);
    }
  }, [field]);

  const renameFile = (originalFile: File, newName: string) => {
    return new File([originalFile], newName, {
      type: originalFile.type,
      lastModified: originalFile.lastModified,
    });
  };

  const getExtensionOfMimeType = (mimeType: string): string => {
    switch (mimeType) {
      case "image/jpg":
        return ".jpg";
      case "image/jpeg":
        return ".jpeg";
      case "image/png":
        return ".png";
      case "application/pdf":
        return ".pdf";
      default:
        return "";
    }
  };

  const defaultFileName = useMemo(() => `${employeeFirstName}_${residenceExpiryDate}`, [residenceExpiryDate, employeeFirstName]);

  const handleUploadFile = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.some((file) => file.size === 0)) {
        toast.error("0Byteのファイルはアップロードできません。");
        return;
      }

      const result = await checkFileListWhetherIsActualPdf(acceptedFiles);
      if (result === CHECK_PDF_RESULT.PASSWORD_PROTECTED) {
        toast.error(INVALID_PASSWORD_PROTECTED_PDF);
        return;
      }
      if (result === CHECK_PDF_RESULT.INVALID) {
        toast.error(INVALID_PDF_FORMAT);
        return;
      }

      if (existingFiles.length + acceptedFiles.length + files.length > 10) {
        toast.error(`ファイルは最大10件までアップロードできます。`);
        return;
      }

      if (acceptedFiles.some((item) => convertByteToMb(item.size) > 10)) {
        toast.error(`「${fieldLabel}」ファイルサイズは10Mb以内でお願いします。`);
        return;
      }

      if (!!rules.acceptMimeTypes && acceptedFiles.some((file) => !rules.acceptMimeTypes?.includes(file.type))) {
        toast.error(`アップロード可能なファイル形式は${getExtensionsOfMimeTypes(rules.acceptMimeTypes).join("、")}です。`);
        return;
      }

      const droppedFiles = acceptedFiles.map((file) => {
        const fileOption: TExtendFileOption = {
          id: makeUuid(),
          status: getFileStatusWithRules(file, rules),
        };

        const newwNameFile = renameFile(file, `${defaultFileName}${getExtensionOfMimeType(file.type)}`);
        return Object.assign(newwNameFile, { ...fileOption });
      });
      const newFiles = [droppedFiles, files].flat();

      setFiles(newFiles);
      triggerChangeFiles(newFiles);
    },
    [files, defaultFileName],
  );

  const removeFile = (removedId: Key) => {
    const updatedFiles = files.filter(({ id }) => removedId !== id);
    triggerChangeFiles(updatedFiles);
    triggerChangeFiles(updatedFiles);
  };

  const triggerChangeFiles = (files: TExtendFile[]) => {
    field?.onChange(files);
    field?.onBlur();
  };

  return (
    <Stack>
      <Button component="label" size="sm" variant="outline" fullWidth disabled={readonly}>
        <Stack
          mr={1}
          alignItems="center"
          justifyContent="center"
          width={16}
          height={16}
          borderRadius={16}
          bgcolor={files.length > 0 ? state.success_1 : divider.high}
        >
          <IconCheck color={background.white} />
        </Stack>
        アップロード
        <VisuallyHiddenInput
          type="file"
          accept={[MIME_TYPES.IMAGE, MIME_TYPES.PDF].flat().join(",")}
          onChange={(event) => event.target.files && handleUploadFile(Array.from(event.target.files))}
          onClick={(event) => {
            handleCheckExistFileName(event);
            event.currentTarget.value = "";
          }}
          multiple
        />
      </Button>
      <Stack flexDirection="column" gap={1} mt={files.length ? 1 : 0}>
        {files.map((file) => (
          <FileItem
            key={file.id}
            filePath={file?.filePath}
            file={file}
            downloadable={true}
            onRemove={!readonly ? () => removeFile(file.id) : undefined}
            maxWidth={336}
            errorMsg={getFileErrorMessage(file, rules)}
          />
        ))}
      </Stack>
    </Stack>
  );
};

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});
